// useMentions — unread @mention notifications for the current user.
// Powers the navbar bell badge and per-squad/jam dot indicators.
// Live-updates via Realtime on the `mentions` table.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type Mention = {
  id: string;
  user_id: string;
  source_kind: "squad_message" | "jam_task";
  source_id: string;
  squad_id: string | null;
  channel_id: string | null;
  jam_id: string | null;
  mentioned_by: string;
  preview: string;
  created_at: string;
  read_at: string | null;
};

type Counts = {
  total: number;
  bySquad: Record<string, number>;
  byChannel: Record<string, number>;
  byJam: Record<string, number>;
};

const EMPTY_COUNTS: Counts = { total: 0, bySquad: {}, byChannel: {}, byJam: {} };

export function useMentions() {
  const { user } = useAuth();
  const [mentions, setMentions] = useState<Mention[]>([]);
  const [counts, setCounts] = useState<Counts>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(true);

  const recomputeCounts = useCallback((rows: Mention[]) => {
    const next: Counts = { total: 0, bySquad: {}, byChannel: {}, byJam: {} };
    for (const m of rows) {
      if (m.read_at) continue;
      next.total += 1;
      if (m.squad_id) next.bySquad[m.squad_id] = (next.bySquad[m.squad_id] ?? 0) + 1;
      if (m.channel_id)
        next.byChannel[m.channel_id] = (next.byChannel[m.channel_id] ?? 0) + 1;
      if (m.jam_id) next.byJam[m.jam_id] = (next.byJam[m.jam_id] ?? 0) + 1;
    }
    return next;
  }, []);

  // Initial fetch: pull up to 50 most recent mentions (read or unread) so the
  // dropdown has context, plus all unread for accurate counts.
  useEffect(() => {
    if (!user) {
      setMentions([]);
      setCounts(EMPTY_COUNTS);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from("mentions")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);
      if (cancelled) return;
      const rows = (data ?? []) as Mention[];
      setMentions(rows);
      setCounts(recomputeCounts(rows));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, recomputeCounts]);

  // Realtime — listen for mentions targeting this user only.
  // Use a unique channel name per hook instance so multiple consumers
  // (e.g. Navbar bell + Squads page) don't collide on the same channel,
  // which would cause `.on()` to be called after `.subscribe()`.
  useEffect(() => {
    if (!user) return;
    const channelName = `mentions-${user.id}-${Math.random().toString(36).slice(2, 10)}`;
    const ch = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "mentions",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          setMentions((prev) => {
            let next = prev;
            if (payload.eventType === "INSERT") {
              next = [payload.new as Mention, ...prev].slice(0, 50);
            } else if (payload.eventType === "UPDATE") {
              next = prev.map((m) =>
                m.id === (payload.new as Mention).id ? (payload.new as Mention) : m,
              );
            } else if (payload.eventType === "DELETE") {
              next = prev.filter((m) => m.id !== (payload.old as Mention).id);
            }
            setCounts(recomputeCounts(next));
            return next;
          });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [user, recomputeCounts]);

  // Mark-read RPC. Optimistic — assume success and let realtime correct.
  const markRead = useCallback(
    async (
      scope: "all" | "squad" | "channel" | "jam" | "mention",
      scopeId?: string,
    ) => {
      if (!user) return;
      // Optimistic: clear locally so the badge feels instant.
      setMentions((prev) => {
        const now = new Date().toISOString();
        const next = prev.map((m) => {
          if (m.read_at) return m;
          const match =
            scope === "all" ||
            (scope === "squad" && m.squad_id === scopeId) ||
            (scope === "channel" && m.channel_id === scopeId) ||
            (scope === "jam" && m.jam_id === scopeId) ||
            (scope === "mention" && m.id === scopeId);
          return match ? { ...m, read_at: now } : m;
        });
        setCounts(recomputeCounts(next));
        return next;
      });
      await supabase.rpc("mark_mentions_read", {
        _scope: scope,
        _scope_id: scopeId ?? null,
      } as any);
    },
    [user, recomputeCounts],
  );

  return { mentions, counts, loading, markRead };
}
